import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CaretLeft, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TestQuestionEditor } from "./TestQuestionEditor";
import { TestThresholdEditor } from "./TestThresholdEditor";
import { computeMaxScore } from "@/lib/test-scoring";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import type { Test, TestQuestion, TestThreshold } from "@/types/test";

interface Props {
  testId?: string;
}

function SectionHeader({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h2
        className="text-lg font-semibold text-foreground"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {title}
      </h2>
      {hint && (
        <p className="hidden text-sm text-muted-foreground sm:block">{hint}</p>
      )}
    </div>
  );
}

export function TestForm({ testId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(testId);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultExpiryHours, setDefaultExpiryHours] = useState(24);
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [thresholds, setThresholds] = useState<TestThreshold[]>([]);
  const [isActive, setIsActive] = useState(true);

  const { data: existing, isLoading } = useQuery({
    enabled: isEdit,
    queryKey: ["admin", "tests", testId],
    queryFn: async () => {
      const res = await fetch(`/api/tests/${testId}`);
      await throwIfNotOk(res, t("tests.admin.fetchError"));
      return res.json() as Promise<Test>;
    },
  });

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDescription(existing.description);
      setDefaultExpiryHours(existing.defaultExpiryHours);
      setQuestions(existing.questions);
      setThresholds(existing.thresholds);
      setIsActive(existing.isActive);
    }
  }, [existing]);

  const goBack = () => navigate({ to: "/dashboard/admin/tests" });

  const mutation = useMutation({
    mutationFn: async () => {
      const url = isEdit ? `/api/tests/${testId}` : "/api/tests";
      const method = isEdit ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, description, defaultExpiryHours, questions, thresholds, isActive }),
      });
      await throwIfNotOk(res, t("tests.admin.saveError"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tests"] });
      toast.success(t("common.success"));
      goBack();
    },
  });

  const maxScore = computeMaxScore(questions);

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center p-16 text-muted-foreground">
        <CircleNotch className="size-6 animate-spin" />
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="mx-auto max-w-4xl space-y-6 sm:space-y-8"
    >
      {/* Header */}
      <div className="space-y-4">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" />
          <span>{t("tests.admin.title")}</span>
        </button>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1
              className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {isEdit ? t("tests.admin.editTitle") : t("tests.admin.createTitle")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground sm:text-base">
              {t("tests.admin.subtitle")}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button type="button" variant="outline" onClick={goBack}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || questions.length === 0}
            >
              {mutation.isPending && <CircleNotch className="size-4 animate-spin" />}
              {t("common.save")}
            </Button>
          </div>
        </div>
      </div>

      {/* Identity */}
      <section className="space-y-5 rounded-xl border border-border/50 bg-card p-6 shadow-sm">
        <SectionHeader title={t("tests.admin.fieldName")} />
        <div className="grid gap-5 sm:grid-cols-[2fr_1fr]">
          <div className="space-y-2">
            <Label htmlFor="test-name">{t("tests.admin.fieldName")}</Label>
            <Input
              id="test-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="test-expiry">{t("tests.admin.fieldExpiryHours")}</Label>
            <div className="relative">
              <Input
                id="test-expiry"
                type="number"
                min={1}
                max={168}
                value={defaultExpiryHours}
                onChange={(e) => setDefaultExpiryHours(Number(e.target.value))}
                className="pr-12 tabular-nums"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                hrs
              </span>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="test-description">{t("tests.admin.fieldDescription")}</Label>
          <Textarea
            id="test-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="resize-none"
          />
        </div>
        <Label
          htmlFor="test-active"
          className="inline-flex items-center gap-2.5 rounded-lg border border-border bg-card px-3.5 py-2 text-sm transition-colors hover:border-primary/40"
        >
          <Checkbox
            id="test-active"
            checked={isActive}
            onCheckedChange={(v) => setIsActive(v === true)}
          />
          {t("tests.admin.fieldIsActive")}
        </Label>
      </section>

      {/* Questions */}
      <section className="space-y-5 rounded-xl border border-border/50 bg-card p-6 shadow-sm">
        <SectionHeader
          title={t("tests.admin.questionsTitle")}
          hint={t("tests.admin.questionCount", { count: questions.length })}
        />
        <TestQuestionEditor questions={questions} onChange={setQuestions} />
      </section>

      {/* Scoring */}
      <section className="space-y-5 rounded-xl border border-border/50 bg-card p-6 shadow-sm">
        <SectionHeader
          title={t("tests.admin.thresholdsTitle")}
          hint={t("tests.admin.thresholdsHint", { max: maxScore })}
        />
        <TestThresholdEditor thresholds={thresholds} maxScore={maxScore} onChange={setThresholds} />
      </section>
    </form>
  );
}
